import * as React from 'react';
import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  User,
  Users,
  Monitor,
  Sparkles,
  BarChart2,
  Video,
  CreditCard,
  Plug,
  Blocks,
  Bot,
  History,
} from 'lucide-mui';
import MeetingBotSettings from './meetbot';

import Dashboard from '../dashboard';
import { SettingsKeys, makeUpdateSettingsStatusIdle } from './settingsSlice';
import RecordingSettings from './Recorder';
import PluginSettings from './Plugin';
import RecoverySettings from './Recover';
import Billing from './billing/Billing';
import ProfileSettings from './profile/Profile';
import TeamSettings from './Team';
import AI from './AI';
import Usage from './Usage';
import Integrations from './integrations';
import DisplaySettings from './Display';
import { useRole } from './hooks';
import useQuery from '../../hooks/urlHooks';
import { fetchFolders } from '../library/librarySlice';
import { selectTeam } from '../team/teamSlice';
import { RootState } from '../../reducers';

interface SettingsType {
  type: SettingsKeys;
  label: string;
  component: React.ReactNode;
  icon: React.ReactElement;
}

const allSettingsTabs: SettingsType[] = [
  {
    type: 'user',
    label: 'Profile',
    component: <ProfileSettings />,
    icon: <User className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />,
  },
  {
    type: 'team',
    label: 'Team',
    component: <TeamSettings />,
    icon: <Users className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />,
  },
  {
    type: 'display',
    label: 'Display',
    component: <DisplaySettings />,
    icon: (
      <Monitor className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
  {
    type: 'ai',
    label: 'AI',
    component: <AI />,
    icon: (
      <Sparkles className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
  {
    type: 'usage',
    label: 'Usage',
    component: <Usage />,
    icon: (
      <BarChart2 className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
  {
    type: 'recorder',
    label: 'Recording',
    component: <RecordingSettings />,
    icon: <Video className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />,
  },
  {
    type: 'billing',
    label: 'Billing',
    component: <Billing />,
    icon: (
      <CreditCard className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
  {
    type: 'plugin',
    label: 'Plugins',
    component: <PluginSettings />,
    icon: <Plug className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />,
  },
  {
    type: 'integrations',
    label: 'Integrations',
    component: <Integrations />,
    icon: (
      <Blocks className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
  {
    type: 'meetingbot',
    label: 'Meeting Bot',
    component: <MeetingBotSettings />,
    icon: <Bot className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />,
  },
  {
    type: 'recovery',
    label: 'Recovery',
    component: <RecoverySettings />,
    icon: (
      <History className="w-4 h-4" sx={{ fontSize: 18 }} strokeWidth={1.5} />
    ),
  },
];

const getTabAccessMessage = (type: SettingsKeys) => {
  if (type === 'billing') {
    return 'Only owners can access billing settings';
  }
  if (type === 'team' || type === 'integrations') {
    return 'Only owners and admins can access these settings';
  }
  return '';
};

export default function LabTabs() {
  const { settingKey } = useParams<{ settingKey: SettingsKeys }>();
  const { teamId } = useQuery();
  const { isRole } = useRole();
  const [value, setValue] = React.useState<SettingsKeys>(
    settingKey || allSettingsTabs[0].type
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { teams, selectedTeam } = useSelector((state: RootState) => state.team);

  useEffect(() => {
    if (!teamId || selectedTeam?.teamId === teamId) return;
    const newSelectedTeam = teams.find((t) => t.teamId === teamId);
    if (!newSelectedTeam) return;
    dispatch(selectTeam(newSelectedTeam));
    dispatch(fetchFolders({ teamId }));
  }, [teamId, teams, selectedTeam]);

  const canAccessTab = (type: SettingsKeys) => {
    if (type === 'billing') {
      return isRole(['Owner']);
    }
    if (type === 'team' || type === 'integrations') {
      return isRole(['Owner', 'Admin']);
    }
    return true;
  };

  React.useEffect(() => {
    if (
      settingKey &&
      allSettingsTabs.find((setting) => setting.type === settingKey)
    ) {
      setValue(settingKey);
    }
  }, [settingKey, allSettingsTabs]);

  // Update page title based on selected setting
  React.useEffect(() => {
    const currentTab = allSettingsTabs.find((tab) => tab.type === value);
    document.title = `${currentTab?.label} Settings - ScreenApp`;
    return () => {
      document.title = 'ScreenApp';
    };
  }, [value, allSettingsTabs]);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: SettingsKeys
  ) => {
    if (canAccessTab(newValue)) {
      setValue(newValue);
      dispatch(makeUpdateSettingsStatusIdle());
    }
  };

  const getTooltipTitle = (type: SettingsKeys, label: string) => {
    if (isSmallScreen) return label;
    if (canAccessTab(type)) return '';
    return getTabAccessMessage(type);
  };

  return (
    <Dashboard>
      <Box
        sx={{
          display: 'flex',
          width: 'calc(100% - 16px)',
          height: 'calc(100vh - 64px - 16px)',
          typography: 'body1',
          margin: '8px',
          bgcolor: theme.palette.background.paper,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <TabContext value={value}>
          <Box
            sx={{
              borderRight: 1,
              borderColor: 'divider',
              height: '100%',
              bgcolor: theme.palette.background.paper,
            }}
          >
            <TabList
              onChange={handleChange}
              aria-label="settings tabs"
              orientation="vertical"
              sx={{
                minWidth: isSmallScreen ? 'auto' : '200px',
                height: '100%',
                '& .MuiTab-root': {
                  width: '100%',
                  color: theme.palette.text.primary,
                  opacity: 1,
                  gap: '4px',
                  '&.Mui-disabled': {
                    opacity: 0.7,
                  },
                },
              }}
            >
              {allSettingsTabs.map(({ type, label, icon }: SettingsType) => (
                <Tooltip
                  key={type}
                  title={getTooltipTitle(type, label)}
                  placement="right"
                  arrow
                >
                  <span>
                    <Tab
                      label={isSmallScreen ? '' : label}
                      value={type}
                      component={Link}
                      to={canAccessTab(type) ? `/settings/${type}` : '#'}
                      icon={icon}
                      iconPosition={isSmallScreen ? 'top' : 'start'}
                      disabled={!canAccessTab(type)}
                      onClick={(e) => !canAccessTab(type) && e.preventDefault()}
                      sx={{
                        alignItems: 'center',
                        textAlign: 'left',
                        minHeight: '48px',
                        justifyContent: 'flex-start',
                        minWidth: isSmallScreen ? '48px' : 'auto',
                        width: '100%',
                        gap: '4px',
                        backgroundColor:
                          value === type
                            ? theme.palette.secondary.light
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                        },
                        '&.Mui-disabled': {
                          opacity: 0.7,
                        },
                      }}
                    />
                  </span>
                </Tooltip>
              ))}
            </TabList>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              height: '100%',
              overflow: 'auto',
            }}
          >
            {allSettingsTabs.map(({ type, component }: SettingsType) => (
              <TabPanel key={type} value={type} sx={{ px: 1, py: 0 }}>
                {component}
              </TabPanel>
            ))}
          </Box>
        </TabContext>
      </Box>
    </Dashboard>
  );
}
